import React, { forwardRef } from 'react'

import { twMerge } from 'tailwind-merge'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

interface TooltipWithShortcutProps {
  label: string
  shortcut?: string
}

/**
 * Tooltip content with label and optional keyboard shortcut
 */
export const TooltipWithShortcut: React.FC<TooltipWithShortcutProps> = ({
  label,
  shortcut,
}) => (
  <span className="flex items-center gap-1.5">
    <span>{label}</span>
    {shortcut && (
      <kbd className="px-1 py-0.5 rounded bg-primary-700 dark:bg-primary-600 text-[11px] font-mono">
        {shortcut}
      </kbd>
    )}
  </span>
)

interface ToolbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
  isActive?: boolean
  tooltip?: React.ReactNode
  className?: string
  compact?: boolean
}

/**
 * Reusable toolbar button for rich text editors
 */
export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (
    {
      onClick,
      isActive,
      tooltip,
      children,
      className: btnClassName,
      compact = false,
      ...props
    },
    ref,
  ) => {
    const button = (
      <button
        ref={ref}
        {...props}
        type="button"
        onClick={onClick}
        className={twMerge(
          'flex items-center justify-center rounded transition-colors',
          compact ? 'w-6 h-6' : 'w-7 h-7',
          isActive
            ? 'bg-primary-200 dark:bg-primary-700 text-primary-900 dark:text-primary-100'
            : 'text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800 hover:text-primary-700 dark:hover:text-primary-200',
          btnClassName,
        )}
      >
        {children}
      </button>
    )

    if (!tooltip) return button

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="top" className="text-[13px]">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    )
  },
)

ToolbarButton.displayName = 'RichTextEditor.ToolbarButton'
